import { supabase } from "@/integrations/supabase/client";
import { type BackupResult } from "@/utils/wordpressApi";

/**
 * Saves or updates a backup record in Supabase using upsert
 */
export const saveOrUpdateBackupInSupabase = async (
  websiteId: string,
  backup: BackupResult
): Promise<boolean> => {
  try {
    console.log(`💾 Saving/updating backup in Supabase:`, { websiteId, backup });
    
    const { data, error } = await supabase
      .from('backup_records')
      .upsert({
        backup_id: backup.id,
        website_id: websiteId,
        backup_type: backup.backupType || 'full',
        size: backup.size,
        status: backup.status,
        download_url: backup.downloadUrl,
        user_id: '00000000-0000-0000-0000-000000000000', // Default user for demo
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'backup_id,website_id'
      });

    if (error) {
      console.error('Error saving/updating backup in Supabase:', error);
      return false;
    }

    console.log(`✅ Backup saved/updated in Supabase successfully:`, data);
    return true;
  } catch (error) {
    console.error('Error saving/updating backup in Supabase:', error);
    return false;
  }
};

/**
 * Saves a backup record to Supabase
 */
export const saveBackupToSupabase = async (
  websiteId: string,
  backup: BackupResult
): Promise<boolean> => {
  return await saveOrUpdateBackupInSupabase(websiteId, backup);
};

/**
 * Updates an existing backup record in Supabase
 */
export const updateBackupInSupabase = async (
  websiteId: string,
  backup: BackupResult
): Promise<boolean> => {
  return await saveOrUpdateBackupInSupabase(websiteId, backup);
};

/**
 * Gets all backups for a website from Supabase
 */
export const getBackupsFromSupabase = async (websiteId: string): Promise<BackupResult[]> => {
  try {
    console.log(`📡 Fetching backups from Supabase for website: ${websiteId}`);
    
    const { data, error } = await supabase
      .from('backup_records')
      .select('*')
      .eq('website_id', websiteId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching backups from Supabase:', error);
      return [];
    }

    const backups: BackupResult[] = data.map(record => {
      // Ensure status is one of the valid values, default to 'failed' if invalid
      let status: "completed" | "failed" | "in-progress" = "failed";
      if (record.status === "completed" || record.status === "failed" || record.status === "in-progress") {
        status = record.status;
      }

      // Ensure backupType is one of the valid values, default to 'full' if invalid
      let backupType: "full" | "incremental" = "full";
      if (record.backup_type === "full" || record.backup_type === "incremental") {
        backupType = record.backup_type;
      }

      return {
        id: record.backup_id,
        createdAt: record.created_at,
        status: status,
        size: record.size || 'Unknown',
        downloadUrl: record.download_url,
        backupType: backupType,
        progress: status === 'completed' ? 100 : 
                 status === 'in-progress' ? 50 : 0
      };
    });

    console.log(`📊 Retrieved ${backups.length} backups from Supabase`);
    return backups;
  } catch (error) {
    console.error('Error fetching backups from Supabase:', error);
    return [];
  }
};

/**
 * Deletes a backup record from Supabase
 */
export const deleteBackupFromSupabase = async (
  websiteId: string,
  backupId: string
): Promise<boolean> => {
  try {
    console.log(`🗑️ Deleting backup from Supabase: ${backupId}`);
    
    const { error } = await supabase
      .from('backup_records')
      .delete()
      .eq('backup_id', backupId)
      .eq('website_id', websiteId);

    if (error) {
      console.error('Error deleting backup from Supabase:', error);
      return false;
    }

    console.log(`✅ Backup deleted from Supabase successfully`);
    return true;
  } catch (error) {
    console.error('Error deleting backup from Supabase:', error);
    return false;
  }
};

/**
 * Creates a simulated backup file for demo purposes
 */
export const createSimulatedBackupFile = async (
  websiteId: string,
  backupId: string
): Promise<string> => {
  // This would normally create an actual file, but for demo purposes we return a URL
  const downloadUrl = `https://backup-site-hero.lovable.app/backups/${backupId}.zip`;
  console.log(`📁 Created simulated backup file: ${downloadUrl}`);
  return downloadUrl;
};
